import React, { Suspense, createContext, lazy, useCallback, useContext, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { readDocParams, withDocParams } from "features/documents/utils/docParams";

// De viewer (en daarmee pdf.js, exceljs, papaparse) pas laden als er echt een document open gaat.
const DocumentViewerDrawer = lazy(() => import("features/documents/components/DocumentViewerDrawer"));

const NOOP = { open: () => {}, close: () => {}, current: { id: null, q: "", page: null } };

const DocumentPreviewContext = createContext(null);

// Vorige/volgende in een reeks (bv. de uittreksels van één rekening): enkel als het open document in
// de reeks staat. → { index, total, prevId, nextId } of null.
export function sequencePosition(sequence, id) {
  if (!Array.isArray(sequence) || sequence.length < 2 || id == null) return null;
  const index = sequence.findIndex((x) => String(x) === String(id));
  if (index < 0) return null;
  return {
    index,
    total: sequence.length,
    prevId: index > 0 ? sequence[index - 1] : null,
    nextId: index < sequence.length - 1 ? sequence[index + 1] : null,
  };
}

// Documentpreview op elke pagina. De URL is de bron: ?doc=<id> (+ docq, docpage) opent de viewer,
// openen zet de parameter (replace) en sluiten haalt hem weg; de rest van de URL blijft staan.
// Gemonteerd in AppShell, dus elke beveiligde route kan `useDocumentPreview().open(id)` gebruiken.
// Optioneel `open(id, { sequence: [ids] })`: de viewer toont dan vorige/volgende binnen die reeks. De reeks
// leeft enkel in het geheugen (niet in de URL): een gedeelde link opent het ene document.
export default function DocumentPreviewProvider({ children }) {
  const [params, setParams] = useSearchParams();
  const { id: docId, q: docQ, page: docPage } = readDocParams(params);
  const [sequence, setSequence] = useState(null);

  const open = useCallback(
    (id, { q, page, sequence: seq } = {}) => {
      if (!id) return;
      setSequence(Array.isArray(seq) && seq.length > 1 ? seq : null);
      setParams((prev) => withDocParams(prev, id, { q, page }), { replace: true });
    },
    [setParams]
  );
  const close = useCallback(() => {
    setSequence(null);
    setParams((prev) => withDocParams(prev, null), { replace: true });
  }, [setParams]);

  const position = useMemo(() => sequencePosition(sequence, docId), [sequence, docId]);
  // Bladeren houdt de reeks vast (zonder zoekterm/pagina van het vorige document).
  const navigate = useCallback((id) => open(id, { sequence }), [open, sequence]);

  const value = useMemo(() => ({ open, close, current: { id: docId, q: docQ, page: docPage } }), [open, close, docId, docQ, docPage]);

  return (
    <DocumentPreviewContext.Provider value={value}>
      {children}
      {docId ? (
        <Suspense fallback={null}>
          <DocumentViewerDrawer
            key={docId}
            idDocument={docId}
            q={docQ}
            page={docPage}
            onClose={close}
            onOpenDocument={(id) => open(id)}
            sequence={position}
            onNavigate={navigate}
          />
        </Suspense>
      ) : null}
    </DocumentPreviewContext.Provider>
  );
}

// { open(id, { q, page, sequence }), close(), current }. Buiten de provider (bv. een losse test) no-ops.
export function useDocumentPreview() {
  return useContext(DocumentPreviewContext) || NOOP;
}
